import { ChevronLeft, LogOut, PackageSearch, UserRound } from 'lucide-react';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { ForgeContextSwitcher } from '../../../forge/components/layout/ForgeContextSwitcher';
import { ThemeToggle } from '../../../forge/components/layout/ThemeToggle';
import { ForgeTheme } from '../../../forge/hooks/useForgeTheme';
import { lurkerNavItemFor, LURKER_NAV_ITEMS } from './lurkerNavigation';

interface LurkerSidebarProps {
  theme: ForgeTheme;
  width: number;
  isCollapsed: boolean;
  isResizing: boolean;
  /** Takes the sidebar off the page entirely; the layout's reveal handle brings it back. */
  hideSidebar: () => void;
  onDragStart: (event: React.MouseEvent) => void;
  toggleTheme: () => void;
  currentPath: string;
  isMobile?: boolean;
  onMobileNavigate?: () => void;
}

export const LurkerSidebar: React.FC<LurkerSidebarProps> = ({
  theme,
  width,
  isCollapsed,
  isResizing,
  hideSidebar,
  onDragStart,
  toggleTheme,
  currentPath,
  isMobile = false,
  onMobileNavigate,
}) => {
  const { logout } = useAuthContext();
  const navigate = useNavigate();
  const effectiveCollapsed = isCollapsed && !isMobile;
  const active = lurkerNavItemFor(currentPath);
  const sidebarClasses = [
    'forge-sidebar',
    'lurker-sidebar',
    effectiveCollapsed ? 'is-collapsed' : '',
    isResizing ? 'is-resizing' : '',
    isMobile ? 'is-mobile' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <aside
      className={sidebarClasses}
      style={{ width: !isMobile && !effectiveCollapsed ? width : undefined }}
      aria-label="Lurker navigation"
    >
      <div className="lurker-sidebar-brand">
        <PackageSearch aria-hidden="true" />
        <span>Lurker</span>
      </div>
      <nav className="sidebar-nav" aria-label="Lurker navigation">
        <div className="sidebar-section">
          {LURKER_NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = item === active;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-item ${isActive ? 'active' : ''}`}
                aria-label={effectiveCollapsed ? item.label : undefined}
                aria-current={isActive ? 'page' : undefined}
                onClick={onMobileNavigate}
              >
                <Icon aria-hidden="true" />
                <span className="sidebar-item-label">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      <div className="sidebar-footer">
        <div className="sidebar-footer-utilities">
          <ForgeContextSwitcher variant={isMobile ? 'drawer' : 'sidebar'} hover={false} />
          <ThemeToggle theme={theme} onToggle={toggleTheme} variant="sidebar" />
          <button
            className="sidebar-footer-link"
            onClick={() => {
              onMobileNavigate?.();
              navigate('/forge/profile');
            }}
            type="button"
          >
            <UserRound aria-hidden="true" />
            <span className="sidebar-collapse-label">Profile</span>
          </button>
          <button
            className="sidebar-footer-link"
            onClick={() => {
              onMobileNavigate?.();
              logout();
              navigate('/stores');
            }}
            type="button"
          >
            <LogOut aria-hidden="true" />
            <span className="sidebar-collapse-label">Sign out</span>
          </button>
        </div>
        {!isMobile ? (
          <>
            <div className="sidebar-footer-divider" />
            <button
              className="sidebar-collapse-btn"
              onClick={hideSidebar}
              type="button"
              aria-label="Hide navigation"
            >
              <ChevronLeft aria-hidden="true" />
              <span className="sidebar-collapse-label">Hide</span>
            </button>
          </>
        ) : null}
      </div>
      {!isMobile ? (
        // A pointer-only resize grip, as in the other workspaces; Hide covers the keyboard.
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div className="sidebar-drag-handle" onMouseDown={onDragStart} />
      ) : null}
    </aside>
  );
};
